import React, {useState} from "react";
import { motion } from "framer-motion";
import { Zap, Cpu, Layout, Globe, LineChart, Shield, Lock } from "lucide-react";
import {
  FileSearch,
  RefreshCw,
  Smartphone,
  Network,
  Database,
  ShieldCheck,
  Gauge,
  GitBranch,
} from "lucide-react";
import { 
  Rocket, 
 
  Crown, 
  PhoneCall, 
  CheckCircle2,
  Mail,
  Copy,
  X
} from "lucide-react";
import { toast } from "react-hot-toast";

import HomeHeader from "../components/Layout/HomeHeader";
import heroImg from "../assets/homeImage.jpg";
import aiImg from "../assets/Intell_automation_image.jpg";
import ContactPage from "../components/UI/ContactPage";
import { useNavigate } from "react-router-dom";
import Footer from "../components/UI/Footer";
 import PricingCTA from "../components/UI/Pricing";
import OurClients from "../components/UI/OurClients";

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};


const testingSuite = [
  {
    icon: <Globe />,
    title: "Web Testing",
    description:
      "An AI agent signs in to your web app, explores it and tests it while you watch.",
    points: [
      "Connect → Discover → Review → Validate: it writes tests, you confirm expected results",
      "Works on public sites or behind sign-in, with approver accounts for approval flows",
      "Standard crawl with test stories and Excel report, or prompt testing with your own steps",
      "Live browser stream, activity log and a Terminate button",
    ],
  },
  {
    icon: <FileSearch />,
    title: "Business Validation",
    description:
      "Test Case Designer for before the site is built. No website needed.",
    points: [
      "Upload a requirements document (PDF, DOCX, TXT or MD, up to 50 MB)",
      "Get back a full test case specification",
      "Reads diagrams and mockups inside the document",
    ],
  },
  {
    icon: <RefreshCw />,
    title: "Regression Testing",
    description:
      "Project-based regression testing that keeps every release in check.",
    points: [
      "Organise work into projects",
      "Connect your existing tools",
      "ML pipeline, insights and an AI chat assistant",
    ],
  },
  {
    icon: <Smartphone />,
    title: "Mobile App Testing",
    description: "Upload an Android APK and an autonomous AI agent tests it.",
    points: [
      "Optional login details and a max actions setting (50–500)",
      "Pipeline stages: Validation → Exploration → Testing",
      "Live device stream, live logs, and screen, passed and failed counts",
    ],
  },
  {
    icon: <Network />,
    title: "API Testing",
    description:
      "API Guardian tests your endpoints straight from your Swagger / OpenAPI docs.",
    points: [
      "Target URL, API base URL and optional OpenAPI link",
      "Login credentials, OTP and results delivered by email",
      "Safe mode (on by default) skips destructive calls",
    ],
  },
  {
    icon: <Database />,
    title: "DB Testing",
    description:
      "Full database assessments for PostgreSQL, MongoDB or both via a replica URL.",
    points: [
      "Choose Claude or GPT and pick the model",
      "Tests in SQL or JavaScript, as integration or unit tests",
      "Optional schema DDL and description to steer the AI",
    ],
  },
  {
    icon: <ShieldCheck />,
    title: "Security Testing (VAPT)",
    description:
      "Security assessment against the OWASP Top 10 plus API security.",
    points: [
      "Checks pages behind login using a test account",
      "Reads your OpenAPI docs to cover every endpoint",
      "Requires confirmation that you're authorized to test the site",
    ],
  },
  {
    icon: <Gauge />,
    title: "Performance Testing",
    description:
      "Describe a user flow in plain English. The engine plans it, finds the real endpoints and runs the load test.",
    points: [
      "Scenarios: browsing, login under load, signup, per-feature timing or mixed",
      "Set the number of virtual users",
      "Smoke and Load always run; add Stress, Spike and Soak",
    ],
  },
  {
    icon: <GitBranch />,
    title: "GitHub Code Testing",
    description:
      "AI reads your code's business logic, then tests whether the code does what it claims.",
    points: [
      "Connect a GitHub repo, or upload a folder or .zip (up to 500 MB)",
      "Python, JavaScript, TypeScript, Java and Kotlin",
      "Run history kept per repository",
    ],
  },
];

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-hidden">
      <HomeHeader />

      {/* ================= HERO ================= */}
      <section id="home" className="hero-bg relative pt-36 pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
          {/* LEFT GLASS */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="lg:col-span-5 hero-glass p-10 md:p-14"
          >
            <p className="text-xs uppercase tracking-widest text-orange-600 font-semibold mb-6">
              Autonomous AI Testing for Faster, Smarter Releases
            </p>

            <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
              Elevate Your <br />
              <span className="text-orange-500">QA</span>
            </h1>

            <div className="hero-curve w-[70%] mb-6" />

            <p className="text-slate-600 leading-relaxed mb-8">
              Discover the future of software testing.{" "}
              <span className="text-blue-800 font-semibold">MNR</span>{" "}
              <span className="text-orange-500 font-bold text-lg">AT</span>
              ’s AI-driven platform automates functional, UI, API, and
              regression workflows with intelligent defect detection.
            </p>

            <div className="flex gap-4 flex-wrap">
              <button
                onClick={() =>
                  document
                    .getElementById("contact")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full shadow-lg shadow-orange-200 transition active:scale-95"
              >
                Contact Us
              </button>
            </div>
          </motion.div>

          {/* RIGHT IMAGE */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="lg:col-span-7 flex justify-center lg:justify-end relative"
          >
            <img
              src={heroImg}
              alt="AI"
              className="rounded-[36px] shadow-xl border border-white max-w-[650px] w-full"
            />

            {/* FLOATING BADGE */}
            <motion.div
              animate={{ y: [0, -12, 0] }}
              transition={{ repeat: Infinity, duration: 4 }}
              className="hero-badge absolute -bottom-6 left-10 flex items-center gap-3 p-4"
            ></motion.div>
          </motion.div>
        </div>
      </section>

      {/* =================>> FEATURES<< ================= */}
      <section id="features" className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-black mb-4">
            Transformative AI Testing
          </h2>
          <p className="text-orange-500 uppercase tracking-widest text-xs font-bold">
            Revolutionize Your Workflow
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-14 items-center">
          <motion.img
            src={aiImg}
            alt="features"
            className="rounded-3xl shadow-lg border border-white"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
          />

          <motion.div variants={fadeUp} initial="hidden" whileInView="show">
            <h3 className="text-3xl md:text-4xl font-bold mb-6">
              Intelligent <span className="text-orange-500">Automation</span>
            </h3>

            <p className="text-slate-600 mb-8">
              AI powered prioritization, test generation, and self-healing
              workflows accelerate your release cycle.
            </p>

            <button
              onClick={() => navigate("/login")}
              className="bg-slate-900 text-white px-8 py-3 rounded-full font-semibold hover:bg-orange-600 transition"
            >
              Start your journey
            </button>
          </motion.div>
        </div>
      </section>

      {/* =================> TESTING SUITE <<================= */}
      <section id="testing-suite" className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-orange-500 uppercase tracking-widest text-xs font-bold mb-3">
            Features
          </p>
          <h2 className="text-3xl md:text-5xl font-black mb-4">
            One Platform, <span className="text-orange-500">Every Kind of Testing</span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            From requirements to production code, AI agents cover web, mobile,
            API, database, security and performance testing. Bring your own
            OpenAI or Anthropic key.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {testingSuite.map((feature) => (
            <FeatureCard key={feature.title} {...feature} />
          ))}
        </div>
      </section>

      {/* =================> CAPABILITIES <<================= */}
      <section id="about" className="bg-orange-50/40 py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            className="text-center mb-16"
          >
            <p className="text-orange-500 uppercase tracking-widest text-xs font-bold mb-3">
              Capabilities
            </p>
            <h2 className="text-3xl md:text-5xl font-black">
              Unlock the Power of{" "}
              <span className="text-orange-500">Automation Testing</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8">
            <Card
              icon={<Cpu />}
              title="Intelligent Discovery"
              description="AI-driven requirement analysis, risk prioritization, and smart test coverage planning."
            />

            <Card
              icon={<Layout />}
              title="Experience Validation"
              description="Autonomous UI, UX, accessibility, and visual regression testing across devices."
            />

            <Card
              icon={<Globe />}
              title="Cloud & DevOps Ready"
              description="Seamless automation for cloud-native, microservices, and CI/CD pipelines."
            />

            <Card
              icon={<LineChart />}
              title="Performance Intelligence"
              description="AI-powered load, stress, and scalability testing with predictive insights."
            />

            <Card
              icon={<Shield />}
              title="Self-Healing Automation"
              description="Adaptive tests that evolve with application changes, reducing maintenance overhead."
            />

            <Card
              icon={<Lock />}
              title="Enterprise-Grade Security"
              description="Secure, compliant, and scalable testing for mission-critical applications."
            />
          </div>
        </div>
      </section>

      {/* ================= Plan Pricing  ================= */}
      <section id="pricing">
        <PricingCTA />
      </section>

      {/* ================= Our Clients ================= */}
      <section id="clients">
        <OurClients />
      </section>

      {/* ================= CONTACT ================= */}
      <section id="contact">
        <ContactPage />
      </section>

      {/* ================= >>FOOTER <<================= */}
      <Footer />
    </div>
  );
};

const Card = ({ icon, title, description }) => (
  <motion.div
    whileHover={{ y: -10 }}
    className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition h-full"
  >
    <div className="text-orange-500 mb-4">{icon}</div>

    <h4 className="font-semibold mb-2">{title}</h4>

    <p className="text-slate-500 text-sm leading-relaxed">{description}</p>
  </motion.div>
);

const FeatureCard = ({ icon, title, description, points }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true }}
    whileHover={{ y: -8 }}
    className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition h-full flex flex-col"
  >
    <div className="w-12 h-12 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center mb-5">
      {icon}
    </div>

    <h4 className="text-lg font-bold mb-2">{title}</h4>

    <p className="text-slate-600 text-sm leading-relaxed mb-5">{description}</p>

    <ul className="space-y-2 pt-5 border-t border-slate-100">
      {points.map((point) => (
        <li key={point} className="flex gap-2 text-sm text-slate-500">
          <CheckCircle2 className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
          <span>{point}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export default Home;
